/**
 * Feature DI Container
 * Centralizador de todas as dependências das features
 */
import { DashboardDI } from './dashboard/di/dashboard_dependencies';
// import { KyoZapDI } from './kyozap/di/kyozap_dependencies';

/**
 * Feature DI Container
 * Gerencia e centraliza o acesso a todas as features do sistema
 */
export class FeatureDIContainer {
  private static instance: FeatureDIContainer;
  private dashboardDI: DashboardDI;
 // private kyozapDI: KyoZapDI;

  private constructor() {
    console.log('🏗️ FeatureDIContainer: Inicializando container de features...');
    
    // Inicializar DI das features
    this.dashboardDI = DashboardDI.getInstance();
   // this.kyozapDI = KyoZapDI.getInstance();
    
    console.log('✅ FeatureDIContainer: Container inicializado com sucesso');
  }

  static getInstance(): FeatureDIContainer {
    if (!FeatureDIContainer.instance) {
      FeatureDIContainer.instance = new FeatureDIContainer();
    }
    return FeatureDIContainer.instance;
  }

  // ============ DASHBOARD FEATURE ============

  /**
   * Acesso ao DI do Dashboard
   */
  get dashboard(): DashboardDI {
    return this.dashboardDI;
  }

  /**
   * Shortcuts para Dashboard Use Cases
   */
  getDashboardStatsUseCase() {
    return this.dashboardDI.get('GetDashboardStatsUseCase');
  }

  getDashboardMetricsUseCase() {
    return this.dashboardDI.get('GetDashboardMetricsUseCase');
  }

  // ============ KYOZAP FEATURE ============

  /**
   * Acesso ao DI do KyoZap
   */
  // get kyozap(): KyoZapDI {
  //   return this.kyozapDI;
  // }

  /**
   * Shortcuts para KyoZap Use Cases
   */
  // getConnectWhatsAppUseCase() {
  //   return this.kyozapDI.get('ConnectWhatsAppUseCase');
  // }

  // getSendWhatsAppMessageUseCase() {
  //   return this.kyozapDI.get('SendWhatsAppMessageUseCase');
  // }

  // ============ GENERIC ACCESS ============

  /**
   * Acesso genérico a qualquer dependência de qualquer feature
   */
  getFromFeature<T>(featureName: string, dependencyName: string): T {
    switch (featureName.toLowerCase()) {
      case 'dashboard':
        return this.dashboardDI.get<T>(dependencyName);
      // case 'kyozap':
      //   return this.kyozapDI.get<T>(dependencyName);
      default:
        throw new Error(`FeatureDIContainer: Feature '${featureName}' não encontrada`);
    }
  }

  // ============ MANAGEMENT METHODS ============

  /**
   * Health check de todas as features
   */
  healthCheck(): { 
    healthy: boolean; 
    features: Record<string, { healthy: boolean; issues: string[] }> 
  } {
    console.log('🔍 FeatureDIContainer: Executando health check...');

    const features = {
      dashboard: this.dashboardDI.healthCheck(),
     // kyozap: this.kyozapDI.healthCheck()
    };

    const allHealthy = Object.values(features).every(f => f.healthy);

    const result = {
      healthy: allHealthy,
      features
    };

    if (allHealthy) {
      console.log('✅ FeatureDIContainer: Health check passou - todas as features OK');
    } else {
      console.error('❌ FeatureDIContainer: Health check falhou', result);
    }

    return result;
  }

  /**
   * Lista todas as features disponíveis
   */
  listFeatures(): string[] {
    return ['dashboard', 'kyozap'];
  }

  /**
   * Lista todas as dependências de uma feature
   */
  listFeatureDependencies(featureName: string): string[] {
    switch (featureName.toLowerCase()) {
      case 'dashboard':
        return this.dashboardDI.listDependencies();
      // case 'kyozap':
      //   return this.kyozapDI.listDependencies();
      default:
        throw new Error(`FeatureDIContainer: Feature '${featureName}' não encontrada`);
    }
  }

  /**
   * Recarrega todas as features
   */
  reloadAll(): void {
    console.log('🔄 FeatureDIContainer: Recarregando todas as features...');
    
    this.dashboardDI.reload();
   // this.kyozapDI.reload();
    
    console.log('✅ FeatureDIContainer: Todas as features recarregadas');
  }

  /**
   * Limpa todas as features (útil para testes)
   */
  clearAll(): void {
    console.log('🧹 FeatureDIContainer: Limpando todas as features...');
    
    this.dashboardDI.clear();
    //this.kyozapDI.clear();
    
    console.log('✅ FeatureDIContainer: Todas as features limpas');
  }

  /**
   * Obtém estatísticas do container
   */
  getStats(): {
    totalFeatures: number;
    totalDependencies: number;
    featuresDetails: Record<string, { dependencyCount: number; dependencies: string[] }>;
  } {
    const featuresDetails = {
      dashboard: {
        dependencyCount: this.dashboardDI.listDependencies().length,
        dependencies: this.dashboardDI.listDependencies()
      },
      // kyozap: {
      //   dependencyCount: this.kyozapDI.listDependencies().length,
      //   dependencies: this.kyozapDI.listDependencies()
      // }
    };

    const totalDependencies = Object.values(featuresDetails)
      .reduce((total, feature) => total + feature.dependencyCount, 0);

    return {
      totalFeatures: this.listFeatures().length,
      totalDependencies,
      featuresDetails
    };
  }

  /**
   * Debug: Imprime informações detalhadas do container
   */
  debug(): void {
    console.group('📊 FeatureDIContainer Debug Info');
    
    const stats = this.getStats();
    console.log('Estatísticas:', stats);
    
    const health = this.healthCheck();
    console.log('Health Check:', health);
    
    console.groupEnd();
  }
}

// ============ GLOBAL INSTANCE ============

/**
 * Instância global do container de features
 */
export const featureDI = FeatureDIContainer.getInstance();

// ============ CONVENIENCE EXPORTS ============

/**
 * Exports diretos para uso fácil
 */
export const useDashboard = () => featureDI.dashboard;
//export const useKyoZap = () => featureDI.kyozap;

export const getDashboardStats = () => featureDI.getDashboardStatsUseCase();
export const getDashboardMetrics = () => featureDI.getDashboardMetricsUseCase();

// export const connectWhatsApp = () => featureDI.getConnectWhatsAppUseCase();
// export const sendWhatsAppMessage = () => featureDI.getSendWhatsAppMessageUseCase();